import {
  Box,
  Button,
  CircularProgress,
  Grid,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { ApiFunctions } from 'hydra-data-types';
import { Dispatch, FunctionComponent, SetStateAction, useState } from 'react';
import * as yup from 'yup';
import { doesUserExist, makeFormErrors } from '../../api/AuthApi';
import buttonArrow from '../../assets/svgs/arrow-right.svg';
import { AlertBar } from '../../components/AlertBar';
import { USER_TYPE } from '../../constants';
import { Analytics } from '../../helpers/analytics/Analytics';
import { AnalyticsEventName } from '../../helpers/analytics/AnalyticsEvent';
import { isNonEmptyString } from '../../helpers/StringUtils';
import { useAuthStore } from '../../store/AuthStore';
import { ValidationMessages } from './types';

const useStyles = makeStyles((theme) => ({
  root: { width: '100%' },
  cardGrid: { width: '100%' },
  submitButtonContainer: {
    alignSelf: 'center',
    [theme.breakpoints.up('md')]: { width: 'fit-content ' },
  },
  buttonRoot: {},
}));

const { EMAIL_REQUIRED, INVALID_EMAIL } = ValidationMessages;

const SCHEMA: yup.ObjectSchema<ApiFunctions.DoesUserExistData> = yup
  .object()
  .shape({
    email: yup.string().email(INVALID_EMAIL).required(EMAIL_REQUIRED),
  })
  .required();

type LoginScreenProps = {
  setStep: Dispatch<SetStateAction<number>>;
  setDoesUserExist: Dispatch<SetStateAction<ApiFunctions.DoesUserExistResult | undefined>>;
  setEmail: Dispatch<SetStateAction<string>>;
  hcpLoggedInWithReferralLink?: boolean;
};

// eslint-disable-next-line max-len
const REFERRAL_LINK_ERROR =
  'You can’t accept this referral since you’re already logged in to Connect as a healthcare practitioner.';

export const LoginScreen: FunctionComponent<LoginScreenProps> = (props) => {
  const { hcpLoggedInWithReferralLink } = props;

  const hcpReferralHash = useAuthStore((state) => state.hcpReferralHash);
  const hasHcpReferralHash = isNonEmptyString(hcpReferralHash);
  const [openAlert, setOpenAlert] = useState(hcpLoggedInWithReferralLink ?? false);
  const [errorMessage, setErrorMessage] = useState(
    hcpLoggedInWithReferralLink ? REFERRAL_LINK_ERROR : undefined
  );

  const classes = useStyles();
  const initialValues: ApiFunctions.DoesUserExistData = { email: '' };

  async function onSubmit(
    values: ApiFunctions.DoesUserExistData,
    helpers: FormikHelpers<ApiFunctions.DoesUserExistData>
  ) {
    try {
      const doesUserExistResult = await doesUserExist({ email: values.email });
      props.setDoesUserExist(doesUserExistResult);

      if (doesUserExistResult.hasMindsetId) {
        Analytics.track({
          event: AnalyticsEventName.a_email_exists,
          userType: hasHcpReferralHash ? USER_TYPE.PATIENT : USER_TYPE.HCP,
        });
      } else {
        Analytics.track({
          event: AnalyticsEventName.a_email_not_exist,
          userType: hasHcpReferralHash ? USER_TYPE.PATIENT : USER_TYPE.HCP,
        });
      }

      props.setEmail(values.email);
      props.setStep(1);
    } catch (error) {
      handleErrors(error, helpers);
    }
  }

  function handleErrors(error: any, { setErrors }: FormikHelpers<ApiFunctions.DoesUserExistData>) {
    const formErrors = makeFormErrors(error);

    if (formErrors !== undefined) {
      setErrors(formErrors as ApiFunctions.DoesUserExistData);
    } else {
      setErrorMessage('Unable to sign up, please try again');
      setOpenAlert(true);
      Analytics.track({
        event: AnalyticsEventName.a_email_failed,
        error,
        userType: hasHcpReferralHash ? USER_TYPE.PATIENT : USER_TYPE.HCP,
      });
    }
  }

  return (
    <Grid container direction="column" data-testid="login-screen">
      <AlertBar
        copy={errorMessage}
        status="error"
        persist
        open={openAlert}
        onClose={hcpLoggedInWithReferralLink ? undefined : setOpenAlert}
      />
      <Formik initialValues={initialValues} validationSchema={SCHEMA} onSubmit={onSubmit}>
        {(formikProps: FormikProps<ApiFunctions.DoesUserExistData>) => {
          const { values, touched, dirty, errors, handleBlur, handleChange, isSubmitting } =
            formikProps;

          return (
            <Form>
              <Grid container item xs={12} direction="column" alignItems="center">
                <Box width="100%" textAlign="center" marginBottom="40px">
                  <TextField
                    required
                    fullWidth
                    name="email"
                    id="email"
                    placeholder="example@email.com"
                    value={values.email}
                    type="email"
                    error={!!(isNonEmptyString(errors.email) && touched.email)}
                    helperText={isNonEmptyString(errors.email) && touched.email && errors.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    inputProps={{ style: { textAlign: 'center' } }}
                  />
                </Box>

                <Grid container item xs={12} justifyContent="center">
                  <Box width="200px" display="flex" position="relative">
                    <Button
                      type="submit"
                      role="button"
                      variant="contained"
                      color="primary"
                      classes={{ root: classes.buttonRoot }}
                      size="small"
                      fullWidth
                      disabled={hcpLoggedInWithReferralLink || !dirty || isSubmitting}
                      endIcon={
                        <img src={buttonArrow} alt="next button" width="24px" height="24px" />
                      }
                    >
                      Next
                    </Button>
                    {isSubmitting && <CircularProgress size={24} color="primary" />}
                  </Box>
                </Grid>

                <Box textAlign="center" width={250} marginTop="50px">
                  {hasHcpReferralHash ? (
                    <Typography variant="body2">
                      If you already have an account for any of our programs, enter the email you
                      signed up with.
                    </Typography>
                  ) : (
                    <Typography variant="body2">
                      If you already have an account for any of our programs, login using that
                      email/password.
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </Grid>
  );
};
